import { ref, reactive, computed, watch, onMounted } from '@vue/composition-api'
import { useToast } from 'vue-toastification/composition'
import { useUtils as useI18nUtils } from "@core/libs/i18n"
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import store from '@/store'
import ability from '@/libs/acl/ability'
import { debounce } from 'lodash'
import useFilterCounter from '@/components/Filters/useFiltersCount'
import user from '@/store/user'

export default function useList() {

  // i18n constant
  const { t } = useI18nUtils();

  // toast local toastification instance
  const toast = useToast()

  // refListTable Holds all data to be shown
  const refListTable = ref(null)

  // pagination Holds pagination metadata using reactive instead of ref for each value
  const pagination = reactive({
    perPage: 10,
    currentPage: 1,
    totalList: 0,
    searchQuery: '',
    sortBy: 'name',
    isSortDirDesc: true,
    perPageOptions: [10, 25, 50, 100]
  });


  // tableColumns represents grid table columns to visualize data using i18n
  const tableColumns = [
    { label: 'alarm_rules.rule_id', key: 'id', sortable: true },
    { label: 'alarm_rules.name', key: 'name', sortable: true },
    { label: 'alarm_rules.description', key: 'description', sortable: true },
    { label: 'alarm_rules.user_id', key: 'userID', sortable: false },
    { label: 'alarm_rules.tenant_id', key: 'tenantID', sortable: false },
  ]

  // validate user permissions to perform updates and deletes
  if (ability.can('editrule', 'layout') === true || ability.can('deleterule', 'layout') === true) {
    tableColumns.splice(0, 0, { label: 'actions.name', key: 'actions' })
  }


  // dataMeta validates the pagination content
  const dataMeta = computed(() => {
    const localItemsCount = refListTable.value ? refListTable.value.localItems.length : 0;
    return {
      from: pagination.perPage * (pagination.currentPage - 1) + (localItemsCount ? 1 : 0),
      to: pagination.perPage * (pagination.currentPage - 1) + localItemsCount,
      of: pagination.totalList
    };
  });

  const tenantFilter = ref(null)
  const tenantOptions = computed(() => {
    return store.getters["tenant/getSelectedItems"].map(option => ({
      label: option.label,
      value: option.value,
    }));
  });

  const userFilter = ref(null)
  const userOptions = computed(() => {
    return store.getters["user/getSelectedItems"].map(option => ({
      label: option.label,
      value: String(option.value),
    }));
  });

  // fetchList gets all records with pagination metadata
  const fetchList = (ctx, callback) => {

    // filter validations
    pagination.sortBy = pagination.sortBy === "userID" ? "user_id" :
      pagination.sortBy === "tenantID" ? "tenant_id" :
        pagination.sortBy;


    store.dispatch('alarm_rules/getAll', {
      q: pagination.searchQuery,
      size: pagination.perPage,
      page: pagination.currentPage,
      sortBy: pagination.sortBy,
      sortDesc: pagination.isSortDirDesc ? 1 : 0,
      tenantID: tenantFilter.value,
      userID: userFilter.value,
    })
      .then(response => {

        if (response.data == null) {
          callback([])
        }

        if (!tenantOptions.value.length || !userOptions.value.length) {
          setTimeout(() => fetchList(ctx, callback), 1);
          return;
        }

        // Create maps for replace tenant and user id's to names
        const tenantMap = new Map(tenantOptions.value.map(t => [t.value, t.label]));
        const userMap = new Map(userOptions.value.map(u => [String(u.value), u.label]));

        // New data to be shown in the grid
        const enrichedData = response.data.map(item => ({
          ...item,
          tenantID: item.tenantID.map(id => tenantMap.get(id)).filter(Boolean).join(', '),
          userID: userMap.get(String(item.userID)),
        }));

        callback(enrichedData);
        pagination.totalList = response.total;
      })
      .catch(error => {
        console.error(error);
        toast({
          component: ToastificationContent,
          props: {
            title: t("messages.error.server_error"),
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        });
      });
  };

  // Fetch tenants and users when the component is mounted
  onMounted(() => {
    store.dispatch("tenant/getAll");
    store.dispatch("user/getAll")
  });


  // refetchData refresh table content with new data
  const refetchData = () => {
    if (refListTable.value) {
      refListTable.value.refresh();
    }
  };

  // watch on pagination properties: currentPage and perPage
  watch(
    () => pagination.currentPage,
    () => {
      refetchData();
    }
  );

  watch(
    () => pagination.perPage,
    () => {
      refetchData();
    }
  );

  // debounce the search query for optimization
  watch(
    () => pagination.searchQuery,
    debounce(() => {
      refetchData();
    }, 500)
  );


  // filters counter ============================
  const filters = ref({});
  const filtersCount = useFilterCounter(filters);
  const buildFilters = () => {
    filters.value = {
      tenantID: tenantFilter.value,
      userID: userFilter.value,
    };
    refetchData();
  }
  const clearSearchFilters = () => {
    tenantFilter.value = null;
    userFilter.value = null;
    buildFilters();
  }
  // ============================================


  return {
    tableColumns,
    pagination,
    dataMeta,
    refListTable,
    fetchList,
    refetchData,
    tenantOptions,
    tenantFilter,
    userOptions,
    userFilter,
    filtersCount,
    clearSearchFilters,
    buildFilters,
    t,
  };
}
